
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { getQuestionClass, getQuestionText } from '@/domain/NPS'
import { timeSince } from '@/utils/time'
import { deleteNote, deleteReply } from '@/api/chat'
import { deleteMessage } from '@/api/notices'
import { Action, Getter, namespace } from 's-vuex-class'
import { ChatStatus } from '@/entities/chat'
import EmojiReaction from '@/components/Chat/EmojiReaction.vue'
import { ConvUIQuestions } from '@/pages/dashboard/entities/dash'
import { isFiveScoreQuestionType } from '@/utils/globalVariables'
import AvatarOrInitials from '@/components/WebView/AvatarOrInitials.vue'
import { UserDetail } from '../../entities'
import fname, { htmlSanitizer, interpolateData } from '@/utils/string'
import Quote from '@/components/Notices/Quote.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { UserDetails } from '@/mobile/src/types/auth'
import { faClock } from '@fortawesome/free-regular-svg-icons'
import { getHashtagWhitelist } from '@/api/hashtagWhitelist'

const AuthModule = namespace('auth')

@Component<ChatMessage>({
    components: {
        EmojiReaction,
        AvatarOrInitials,
        Quote,
        FontAwesomeIcon,
    },
})
export default class ChatMessage extends Vue {
    public timeSince = timeSince
    public expanded = false // if workflow should expand
    public undoText = 'Undo'

    public hashtagWhitelist = []

    private readonly faClock = faClock

    @Prop() public message
    @Prop() public questionText
    @Prop(Function) public onChangeReplyReview
    @Prop(Object) public chatStatus!: ChatStatus
    @Prop(Object) public customData!: {
        answer: string
        survey_template: string
        question_type: string
    }
    @Prop(Object) public person!: UserDetail
    @Prop(Boolean) public canDeleteNote!: boolean
    @Prop(Boolean) public isUserBasic!: boolean
    @Prop() public convQuestions!: ConvUIQuestions
    @Prop(Boolean)
    hasNetworkConnection!: boolean

    @AuthModule.Getter public details!: UserDetails

    @Action public loadChat

    @Getter private permissions
    @Getter private $isMobile

    @Getter public $companyVars

    public get isFiveScore() {
        return isFiveScoreQuestionType(this.message.question_type)
    }

    public get isShoutout() {
        return this.message.type === 'shoutout'
    }

    public get isQuestion() {
        return this.message.type === 'question'
    }

    public get isChat() {
        return this.message.type === 'chat'
    }

    public get sender() {
        if (this.isChat) {
            return this.message.from_name
        }
        return this.message.name
    }

    public get initials() {
        if (this.isQuestion || this.isChat) {
            return this.person
        } else {
            return {
                id: this.message.user_id,
                name: this.sender,
                avatar: this.message.avatar,
            }
        }
    }

    public get showMessageUser() {
        return !this.isQuestion
    }

    public get textLength() {
        let msg
        if (this.isQuestion) {
            // to determine the emoji popu poisition
            // is mobie => consider as a long message => display emoji at left
            // is desktop => consider as a short message => display emoji at right
            return this.isMobile
                ? this.maxLengthToReverseDirection + 1
                : this.maxLengthToReverseDirection - 1
        }
        if (this.message.type === 'note') {
            msg = this.message.text
        } else {
            msg = this.message.message || ''
        }
        if (msg) {
            return msg.length
        }
        return 0
    }

    public get outgoing() {
        return Number(this.message.outgoing)
    }

    public get reviewReplied() {
        return Number(this.message.review_replied)
    }

    public get secondsToDelete() {
        return Number(this.message.time) - new Date().getTime() / 1000
    }

    public get isMobile() {
        return window.innerWidth < 500
    }

    // whether the score was updated
    public get isScoreUpdated() {
        if (this.message && this.message.conversational) {
            return this.message.conversational['update_score']
        }
        return false
    }

    public get canViewInternalNotes() {
        if (this.$isMobile) {
            // We can access the permissions on desktop this way :(
            return this.permissions.includes('ROLE_VIEW_NOTES')
        }

        return this.details.permissions.includes('ROLE_VIEW_NOTES')
    }

    public get maxLengthToReverseDirection() {
        return this.isMobile ? 14 : 30
    }

    public async beforeMount() {
        if (this.$companyVars.has_hashtag_whitelist) {
            await getHashtagWhitelist().then(({ data }) => {
                this.hashtagWhitelist = data.data ?? []
            })
        }
    }

    public mounted() {
        if (this.secondsToDelete > 0) {
            setTimeout(() => {
                this.undoText = 'Sent'
            }, this.secondsToDelete * 1000)
        }
    }

    public async undoChat() {
        if (
            this.undoText === 'Sent' ||
            !confirm('Are you sure you want to delete this email?')
        ) {
            return
        }

        this.$emit('undo-chat', this.message)

        await deleteReply(Number(this.message.id))
        this.loadChat()
    }

    public async doDeleteNote() {
        if (confirm('Are you sure you want to delete this note?')) {
            await deleteNote(Number(this.message.id))
            this.loadChat()
        }
    }

    public async doDeleteShoutout() {
        if (confirm('Are you sure you want to delete this shoutout?')) {
            await deleteMessage(Number(this.message.notice_id))
            this.loadChat()
        }
    }

    public async doDeleteWorkflow() {
        if (confirm('Delete this queued email?')) {
            const response = await deleteReply(Number(this.message.id))
            if (response.data.msg) {
                alert(response.data.msg)
            }
            this.loadChat()
        }
    }

    public friendlyFieldName(str) {
        let suffix = ''
        if (str === 'initial_score') {
            str =
                this.customData.question_type === 'nps'
                    ? 'NPS Rating'
                    : 'Rating'

            // if there's `update score` item, we display a `(update)` suffix for the `Rating` label
            if (this.isScoreUpdated) {
                suffix = ' (updated)'
            }
        }

        const label = this.customData.custom_field_labels[str + '_c']
            ? this.customData.custom_field_labels[str + '_c']
            : str

        return fname(label) + suffix
    }

    public getQuestionTooltip(param, data) {
        const value = data.value
        const param_c = param + '_c'
        // scorecard mapping
        const mapping = this.message.scorecardBadgeMapping[param_c]
        const score = parseInt(this.customData.answer, 10)

        // special case for scorecard question, need to check if it's positive or negative
        if (value && mapping) {
            const isPositive = this.isFiveScore ? score >= 4 : score >= 9
            const type = isPositive ? 'positive' : 'negative'
            try {
                const replaced = htmlSanitizer(
                    mapping[type].questions[this.customData.survey_template]
                )
                return interpolateData(
                    replaced,
                    this.customData,
                    this.customData.question_type
                )
            } catch (e) {
                return ''
            }
        }

        try {
            const replaced = htmlSanitizer(getQuestionText(data.question_text))
            return interpolateData(
                replaced,
                this.customData,
                this.customData.question_type
            )
        } catch (e) {
            return ''
        }
    }

    public decodeHtmlEntities(str) {
        const textArea = document.createElement('textarea')
        textArea.innerHTML = str
        return textArea.value
    }

    public getQuestionHtml(param, data) {
        let finalValue // 1-10 for nps, text for free text, etc
        const param_c = param + '_c'
        const mapping = this.message.scorecardBadgeMapping[param_c]
        const convQuestionsLoaded = Object.keys(this.convQuestions).length > 0
        switch (param) {
            case 'initial_score':
                finalValue = this.customData.answer
                break
            case 'comment':
                finalValue = this.decodeHtmlEntities(this.message.comment)
                break
            default:
                if (mapping) {
                    const value = this.customData[param_c]
                    const score = parseInt(this.customData.answer, 10)
                    const isPositive = this.isFiveScore
                        ? score >= 4
                        : score >= 9
                    const badgeType = isPositive ? 'positive' : 'negative'

                    finalValue = value

                    if (
                        badgeType in mapping &&
                        'options' in mapping[badgeType]
                    ) {
                        const options = mapping[badgeType].options
                        if (Array.isArray(options)) {
                            // Arrays use auto-incrementing 0-index.
                            // We need to traverse it and find the elem with the matching value,
                            // which must also have a sibling "label" prop.
                            const option = options.find(
                                (option) =>
                                    option.value === finalValue[0] &&
                                    option.label
                            )
                            if (option) {
                                finalValue = option.value
                            }
                        } else {
                            // Objects use the value itself as the index.
                            // We just check if the "value" and "label" props exist.
                            if (finalValue in options) {
                                const option = options[finalValue]
                                if ('value' in option && 'label' in option) {
                                    finalValue = options[finalValue].value
                                }
                            }
                        }
                    } else if (Array.isArray(finalValue)) {
                        finalValue = finalValue[0]
                    }
                } else if (this.convQuestions[param_c]) {
                    // currently there isn't any supported method for editing open questions,
                    // @todo when editing open questions is enabled, obtain data appropriately
                    finalValue = data.value

                    if (
                        param_c in this.convQuestions &&
                        'options' in this.convQuestions[param_c] &&
                        data.value in this.convQuestions[param_c].options
                    ) {
                        finalValue =
                            this.convQuestions[param_c].options[data.value]
                    }
                } else {
                    finalValue = data.value
                }
        }

        const sanitizedValue = this.decodeHtmlEntities(
            htmlSanitizer(finalValue)
        )

        // display a "dot" for score
        let dot = ''
        if (param === 'initial_score') {
            const questionClass = getQuestionClass(
                finalValue,
                this.customData.question_type
            )
            dot = `<span class="dot ${questionClass}"></span>`
        }

        return `<div>${dot}${sanitizedValue}</div>`
    }

    public parseMentionsAndTags(textToParse) {
        let parsedText = textToParse.replace(
            /\[@([^\]]+)\]\(user:([^\)]+)\)/g,
            '<span class="mention" data-uk-tooltip title="$2">@$1</span>'
        )
        if (this.$companyVars.has_hashtag_whitelist) {
            parsedText = parsedText.replace(
                /\[#([^\]]+)\]\(tag:([^\)]+)\)/g,
                (match, name) => {
                    if (this.hashtagWhitelist.includes(name)) {
                        return ' <span class="mention">#' + name + '</span>'
                    }
                    return ' #' + name
                }
            )
        } else {
            parsedText = parsedText.replace(
                /\[#([^\]]+)\]\(tag:([^\)]+)\)/g,
                ' <span class="mention">#$1</span>'
            )
        }

        return parsedText
    }

    public stripTags(text) {
        const div = document.createElement('div')
        div.innerHTML = text
        return div.textContent || div.innerText || ''
    }

    public nl2br(text) {
        return text.replace(/(?:\r\n|\r|\n)/g, '<br>')
    }

    public replaceSpecialChars(text) {
        return text?.replace(/<br\s*[\/]?>/gi, '\n').replace(/&amp;/, '&') ?? ''
    }

    public onClickHashtag(e) {
        if (e.target && e.target.matches('.mention')) {
            let hashtag = e.target.innerText
            if (hashtag.indexOf('#') !== -1) {
                hashtag = hashtag.replace(/^#/, '')
                parent.window.location.href = '/responses?tag=' + hashtag
            }
        }
    }
}
